// src/context/PermisosContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { obtenerFuncionesPorEmail } from "../services/permisosService";
import { FEATURE_BITS as FB, BITSET_LEN, forceFixedPermissions } from "../constants/permisos";

const PermisosContext = createContext({
  bitset: "",
  funciones: {},
  can: () => false,
  canEdit: () => false,
  loading: false,
  refreshPermisos: () => {},
});

/* ===== helpers bitset ===== */
const sanitize = (s = "") => {
  const padded = (s || "").toString().padEnd(BITSET_LEN, "0").slice(0, BITSET_LEN);
  // fuerza bit #1 (INICIO_VIEW) a 1 para evitar bloqueos absurdos
  return forceFixedPermissions(padded);
};
const bitOn = (bitset, pos1) => {
  if (typeof pos1 !== "number" || pos1 < 1 || pos1 > bitset.length) return false;
  return bitset[pos1 - 1] === "1";
};

export const PermisosProvider = ({ children }) => {
  // ⚡ inicializa desde localStorage para que la app funcione al instante
  const [bitset, setBitset] = useState(() =>
    sanitize(localStorage.getItem("endpoint") || "")
  );
  const [funciones, setFunciones] = useState({});
  const [loading, setLoading] = useState(false);

  const cancelRef = useRef(false);

  const resetState = useCallback(() => {
    setFunciones({});
    setBitset((prev) => sanitize(prev));
  }, []);

  const load = useCallback(async () => {
    const email = localStorage.getItem("email") || "";
    const token = localStorage.getItem("token") || "";

    if (!email || !token) {
      resetState();
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await obtenerFuncionesPorEmail(email, token);
      if (cancelRef.current) return;

      // Preferimos lo que diga el backend; si no viene, usa localStorage
      const bs = sanitize(data?.bitset || localStorage.getItem("endpoint") || "");
      // Legacy tab selectors also read localStorage; keep them aligned with server-effective rights.
      localStorage.setItem("endpoint", bs);
      setBitset(bs);
      setFunciones(data?.funciones || {});
    } catch (e) {
      const code = e?.status ?? 0;
      if (![401, 403, 404].includes(code)) {
        console.warn("Permisos load error:", e);
      }
      resetState();
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [resetState]);

  // API pública para refrescar
  const refreshPermisos = useCallback(() => {
    load();
  }, [load]);

  useEffect(() => {
    cancelRef.current = false;
    // primera carga
    load();

    // escucha cambios de auth + endpoint dentro de esta pestaña
    const onAuthChanged = () => {
      const ls = localStorage.getItem("endpoint") || "";
      setBitset(sanitize(ls));
      load();
    };
    window.addEventListener("auth-changed", onAuthChanged);

    // sincroniza multi-pestaña
    const onStorage = (ev) => {
      if (ev.key === "email" || ev.key === "token" || ev.key === "endpoint") {
        const ls = localStorage.getItem("endpoint") || "";
        setBitset(sanitize(ls));
        load();
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      cancelRef.current = true;
      window.removeEventListener("auth-changed", onAuthChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [load]);

  // can(): usa funciones si existen; si no, calcula por bitset
  const can = useCallback(
    (featureKey) => {
      if (
        funciones &&
        Object.prototype.hasOwnProperty.call(funciones, featureKey)
      ) {
        return !!funciones[featureKey];
      }
      const pos = FB[featureKey];
      if (typeof pos !== "number") return false;
      return bitOn(bitset, pos);
    },
    [funciones, bitset]
  );

  const canEdit = useCallback((featureEditKey) => can(featureEditKey), [can]);

  const value = useMemo(
    () => ({ bitset, funciones, can, canEdit, loading, refreshPermisos }),
    [bitset, funciones, can, canEdit, loading, refreshPermisos]
  );

  return (
    <PermisosContext.Provider value={value}>
      {children}
    </PermisosContext.Provider>
  );
};

export const usePermisos = () => useContext(PermisosContext);
